/**
 * Menu sales mix calculations — aligned with app/utils/menuSalesMix.js and
 * express_app/utils/menuSalesMix.js (API source of truth).
 */

export const SALES_MIX_PERIODS = ['month', 'week', 'day']

export const DEFAULT_SALES_MIX_PERIOD = 'month'

const parseAmount = value => {
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i

const toObjectIdString = value => {
  if (value == null) {
    return null
  }

  if (typeof value === 'string') {
    return OBJECT_ID_PATTERN.test(value) ? value : null
  }

  if (typeof value.toString === 'function') {
    const asString = value.toString()
    return OBJECT_ID_PATTERN.test(asString) ? asString : null
  }

  return null
}

export const getRecipeId = recipeOrId => {
  if (!recipeOrId) {
    return null
  }

  if (typeof recipeOrId === 'string') {
    return recipeOrId
  }

  if (recipeOrId.recipeId != null) {
    return String(recipeOrId.recipeId)
  }

  if (recipeOrId.recipe != null) {
    return getRecipeId(recipeOrId.recipe)
  }

  if (recipeOrId._id != null) {
    return String(recipeOrId._id)
  }

  return toObjectIdString(recipeOrId)
}

/** Normalise recipe ids for consistent qty map keys (panel ↔ API). */
export const canonicalRecipeId = recipeOrId => {
  const id = getRecipeId(recipeOrId)
  if (!id) {
    return null
  }

  return String(id).trim().toLowerCase()
}

export const readQtyForRecipe = (qtyByRecipe = {}, recipeOrId) => {
  const target = canonicalRecipeId(recipeOrId)
  if (!target) {
    return ''
  }

  for (const [key, value] of Object.entries(qtyByRecipe)) {
    if (canonicalRecipeId(key) === target) {
      return value ?? ''
    }
  }

  return ''
}

/** GP% from persisted recipe fields (dashboard / API source of truth). */
export const resolveRecipeGpPercent = recipe => {
  if (!recipe) {
    return null
  }

  const storedGp = parseAmount(recipe.grossProfit)
  if (storedGp != null) {
    return storedGp
  }

  const sellingPrice = parseAmount(recipe.sellingPrice)
  const costPerPortion = parseAmount(recipe.costPerPortion)

  if (sellingPrice != null && costPerPortion != null && sellingPrice > 0) {
    const computed = ((sellingPrice - costPerPortion) / sellingPrice) * 100
    if (Number.isFinite(computed)) {
      return computed
    }
  }

  return null
}

export const roundMoney = value => {
  const num = parseAmount(value)
  if (num == null) {
    return null
  }

  return Math.round(num * 100) / 100
}

export const buildSalesMixQtyMap = (salesMix = []) => {
  const map = new Map()

  for (const entry of salesMix || []) {
    const recipeId = canonicalRecipeId(entry)
    const qty = parseAmount(entry.expectedQty ?? entry.expectedMonthlyQty)

    if (!recipeId || qty == null || qty <= 0) {
      continue
    }

    map.set(recipeId, qty)
  }

  return map
}

export const calculateMenuSalesMixMetrics = ({
  recipes = [],
  salesMix = [],
  salesMixPeriod = DEFAULT_SALES_MIX_PERIOD,
} = {}) => {
  const qtyMap = buildSalesMixQtyMap(salesMix)
  const period = SALES_MIX_PERIODS.includes(salesMixPeriod)
    ? salesMixPeriod
    : DEFAULT_SALES_MIX_PERIOD

  if (qtyMap.size === 0) {
    return {
      hasSalesMix: false,
      weightedGp: null,
      estimatedTotalGp: null,
      totalRevenue: null,
      salesMixPeriod: period,
    }
  }

  let weightedGpNumerator = 0
  let totalRevenue = 0
  let estimatedTotalGp = 0
  let hasPricedWeightedRow = false
  let hasMarginRow = false

  for (const recipe of recipes) {
    if (recipe?.monitored === false) {
      continue
    }

    const recipeId = canonicalRecipeId(recipe)
    const qty = qtyMap.get(recipeId)

    if (!qty) {
      continue
    }

    const sellingPrice = parseAmount(recipe.sellingPrice)
    const costPerPortion = parseAmount(recipe.costPerPortion)
    const gpPercent = resolveRecipeGpPercent(recipe)

    if (sellingPrice != null && sellingPrice > 0) {
      const revenue = qty * sellingPrice
      totalRevenue += revenue

      if (gpPercent != null) {
        weightedGpNumerator += revenue * gpPercent
        hasPricedWeightedRow = true
      }
    }

    if (sellingPrice != null && costPerPortion != null) {
      estimatedTotalGp += qty * (sellingPrice - costPerPortion)
      hasMarginRow = true
    }
  }

  return {
    hasSalesMix: true,
    weightedGp:
      hasPricedWeightedRow && totalRevenue > 0
        ? Math.round(weightedGpNumerator / totalRevenue)
        : null,
    estimatedTotalGp: hasMarginRow ? roundMoney(estimatedTotalGp) : null,
    totalRevenue: totalRevenue > 0 ? roundMoney(totalRevenue) : null,
    salesMixPeriod: period,
  }
}

export const normalizeSalesMixEntryInput = entry => {
  const recipeId = getRecipeId(entry)
  const expectedQty = parseAmount(
    entry?.expectedQty ?? entry?.expectedMonthlyQty,
  )

  if (!recipeId || expectedQty == null || expectedQty < 0) {
    return null
  }

  return {
    recipeId,
    expectedQty,
  }
}

export const sanitizeSalesMixEntries = (entries = [], validRecipeIds = []) => {
  const validIdByCanonical = new Map()

  for (const rawId of validRecipeIds || []) {
    const resolved = getRecipeId(rawId)
    if (resolved) {
      validIdByCanonical.set(String(resolved).toLowerCase(), resolved)
    }
  }

  const byRecipeId = new Map()

  for (const rawEntry of entries || []) {
    const normalized = normalizeSalesMixEntryInput(rawEntry)
    if (!normalized) {
      continue
    }

    const recipeKey = String(normalized.recipeId).toLowerCase()
    if (!validIdByCanonical.has(recipeKey)) {
      continue
    }

    if (normalized.expectedQty <= 0) {
      byRecipeId.delete(recipeKey)
      continue
    }

    byRecipeId.set(recipeKey, normalized.expectedQty)
  }

  return Array.from(byRecipeId.entries()).map(([recipeKey, expectedQty]) => ({
    recipe: validIdByCanonical.get(recipeKey) || recipeKey,
    expectedQty,
  }))
}

export const pruneSalesMixEntries = (salesMix = [], validRecipeIds = []) => {
  const validCanonical = new Set(
    (validRecipeIds || []).map(id => canonicalRecipeId(id)).filter(Boolean),
  )

  return (salesMix || []).filter(entry => {
    const recipeId = canonicalRecipeId(entry)
    return recipeId && validCanonical.has(recipeId)
  })
}

export const serializeSalesMixForClient = (salesMix = []) =>
  (salesMix || [])
    .map(entry => {
      const recipeId = getRecipeId(entry)
      const expectedQty = parseAmount(
        entry.expectedQty ?? entry.expectedMonthlyQty,
      )

      if (!recipeId || expectedQty == null || expectedQty <= 0) {
        return null
      }

      return {
        recipeId,
        expectedQty,
      }
    })
    .filter(Boolean)

export const attachMenuSalesMixFields = ({ menu = {}, recipes = [] } = {}) => {
  const salesMix = menu.salesMix || []
  const metrics = calculateMenuSalesMixMetrics({
    recipes,
    salesMix,
    salesMixPeriod: menu.salesMixPeriod,
  })

  return {
    ...menu,
    salesMix: serializeSalesMixForClient(salesMix),
    salesMixPeriod: metrics.salesMixPeriod,
    hasSalesMix: metrics.hasSalesMix,
    weightedGp: metrics.weightedGp,
    estimatedTotalGp: metrics.estimatedTotalGp,
    salesMixTotalRevenue: metrics.totalRevenue,
  }
}

export const recipeHasSalesMixPricing = recipe => {
  const sellingPrice = parseAmount(recipe?.sellingPrice)
  const costPerPortion = parseAmount(recipe?.costPerPortion)
  return sellingPrice != null && sellingPrice > 0 && costPerPortion != null
}

export const buildSalesMixQtyDraft = (salesMix = []) => {
  const draft = {}

  for (const entry of salesMix || []) {
    const recipeId = getRecipeId(entry)
    const qty = parseAmount(entry.expectedQty ?? entry.expectedMonthlyQty)

    if (recipeId && qty != null && qty > 0) {
      draft[recipeId] = String(qty)
    }
  }

  return draft
}

export const buildFullSalesMixEntries = (recipeIds = [], qtyByRecipe = {}) => {
  const ids = new Set(
    [...(recipeIds || []), ...Object.keys(qtyByRecipe || {})]
      .map(recipeId => getRecipeId(recipeId))
      .filter(Boolean),
  )

  return [...ids].map(recipeId => ({
    recipeId,
    expectedQty: Number(qtyByRecipe[recipeId]) || 0,
  }))
}

export const entriesHadPositiveQty = (entries = []) =>
  entries.some(entry => {
    const qty = Number(entry?.expectedQty ?? entry?.expectedMonthlyQty)
    return Number.isFinite(qty) && qty > 0
  })

export const salesMixQtyDraftHasInvalidValues = (qtyByRecipe = {}) =>
  Object.values(qtyByRecipe).some(value => {
    if (value === '' || value == null) {
      return false
    }

    const qty = Number(value)
    return !Number.isFinite(qty) || qty < 0
  })

/** True when the user has typed into any qty field (including "0" or cleared values). */
export const salesMixDraftHasLocalEdits = (qtyByRecipe = {}) =>
  Object.values(qtyByRecipe).some(value => value !== '' && value != null)
